import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Stack,
  Title,
  Group,
  Alert,
  Text,
  Badge,
  Skeleton,
  ActionIcon,
  Paper,
  Tooltip,
  Table,
  ScrollArea,
  Anchor,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconRefresh,
  IconDatabase,
  IconChevronLeft,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppUser } from '../hooks/useAppUser';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  fetchAxisRecipes,
  AxisRecipesResult,
} from '../services/adminCatalogService';
import {
  AXIS_TYPE_LABELS,
  AXIS_TYPE_COLORS,
  BADGE_NO_TRUNCATE,
  axisDisplayName,
  AxisStatusBadge,
} from '../components/Admin/tasteAxisDisplay';
import { CUISINE_LABELS } from '../types/tasteProfile';

const difficultyColor = (d: string | null) =>
  d === 'easy' ? 'green' : d === 'medium' ? 'yellow' : 'red';

const formatDate = (iso?: string | null) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : '—';
};

export const TasteAxisDetailPage = () => {
  const { axisId = '' } = useParams();
  const { user, loading: userLoading } = useAppUser();
  const navigate = useNavigate();
  const [result, setResult] = useState<AxisRecipesResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const axis = result?.axis ?? null;
  usePageTitle(
    axis
      ? `${axisDisplayName(axis.type, axis.name)} | Meals You Love`
      : 'Taste Axis | Meals You Love'
  );

  const load = useCallback(async () => {
    if (!user?.isAdmin || !axisId) return;
    setLoading(true);
    setError(null);
    try {
      setResult(await fetchAxisRecipes(user, axisId));
    } catch (e: any) {
      setError(e.message || 'Failed to load axis recipes');
    } finally {
      setLoading(false);
    }
  }, [user, axisId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!userLoading && !user?.isAdmin) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle size="1rem" />} title="Access Denied" color="red" radius="lg">
          You do not have admin privileges.
        </Alert>
      </Container>
    );
  }

  const isLoading = userLoading || loading;
  const heading = axis ? axisDisplayName(axis.type, axis.name) : axisId;

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          {/* nowrap keeps the refresh icon top-right on phones instead of
              dropping it onto its own row below the title */}
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            {/* nowrap, or the title stack drops below the chevron when the
                subtitle + right-side controls exceed one line */}
            <Group wrap="nowrap">
              <ActionIcon variant="subtle" size="lg" onClick={() => navigate('/admin/taste-axes')}>
                <IconChevronLeft size={20} />
              </ActionIcon>
              <Stack gap={0}>
                <Group gap="xs">
                  <Title order={2}>{heading}</Title>
                  {axis && (
                    <>
                      <Badge
                        size="sm"
                        variant="light"
                        color={AXIS_TYPE_COLORS[axis.type] ?? 'gray'}
                        styles={BADGE_NO_TRUNCATE}
                      >
                        {AXIS_TYPE_LABELS[axis.type] ?? axis.type}
                      </Badge>
                      <AxisStatusBadge
                        axis={{
                          exists: axis.exists,
                          liveRecipeCount: axis.memberCount,
                          minRecipeCount: axis.minRecipeCount,
                        }}
                      />
                    </>
                  )}
                </Group>
                <Text c="dimmed" size="sm">
                  Member recipes ranked by closeness to the axis centroid
                </Text>
              </Stack>
            </Group>
            <ActionIcon variant="outline" size="lg" title="Refresh" onClick={load}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Group>
        </div>

        {axis && (
          <Paper p="md" radius="lg" withBorder>
            <Group gap="xl">
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Members</Text>
                <Text size="sm">{axis.memberCount.toLocaleString()}</Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>At last rebuild</Text>
                <Text size="sm">{axis.storedRecipeCount?.toLocaleString() ?? '—'}</Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Rebuild floor</Text>
                <Text size="sm">{axis.minRecipeCount}</Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Last rebuilt</Text>
                <Text size="sm">{axis.exists ? formatDate(axis.updatedAt) : '—'}</Text>
              </Stack>
            </Group>
          </Paper>
        )}

        {axis && !axis.exists && (
          <Alert icon={<IconAlertCircle size="1rem" />} title="No stored centroid" color="blue" radius="lg">
            This axis has no stored embedding, so its recipes are listed
            alphabetically instead of ranked by similarity.
          </Alert>
        )}

        {isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} height={44} radius="sm" />
            ))}
          </Stack>
        ) : error ? (
          <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red" radius="lg">
            {error}
          </Alert>
        ) : !result || result.recipes.length === 0 ? (
          <Alert icon={<IconDatabase size="1rem" />} title="No recipes" color="blue" radius="lg">
            No catalog recipes carry this attribute.
          </Alert>
        ) : (
          <Paper radius="lg" withBorder>
            <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead>
                  {/* Phones keep the essence of the page — rank, recipe, and
                    similarity — and drop the descriptive columns */}
                <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Recipe</Table.Th>
                    <Table.Th>Similarity</Table.Th>
                    <Table.Th visibleFrom="sm">Cuisine</Table.Th>
                    <Table.Th visibleFrom="sm">Difficulty</Table.Th>
                    <Table.Th visibleFrom="sm">Uses</Table.Th>
                    <Table.Th visibleFrom="sm">Added</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {result.recipes.map((recipe, index) => (
                    <Table.Tr key={recipe.id}>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{index + 1}</Text>
                      </Table.Td>
                      <Table.Td maw={320}>
                        <Group gap="xs" wrap="nowrap">
                          <Anchor
                            size="sm"
                            fw={500}
                            lineClamp={1}
                            onClick={() => navigate(`/admin/catalog-recipe/${recipe.id}`)}
                          >
                            {recipe.name}
                          </Anchor>
                          {recipe.isCurated && (
                            <Badge size="xs" variant="light" color="yellow" styles={BADGE_NO_TRUNCATE}>
                              Top pick
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {recipe.similarity !== null ? (
                          <Text size="sm" ff="monospace">{recipe.similarity.toFixed(3)}</Text>
                        ) : (
                          <Tooltip label="This recipe has no usable embedding, so it never contributed to the centroid and can't be ranked">
                            <Badge size="xs" variant="light" color="red" styles={BADGE_NO_TRUNCATE}>
                              no embedding
                            </Badge>
                          </Tooltip>
                        )}
                      </Table.Td>
                      <Table.Td visibleFrom="sm">
                        {recipe.cuisine ? (
                          <Badge
                            size="sm"
                            variant="light"
                            color={AXIS_TYPE_COLORS.cuisine}
                            styles={BADGE_NO_TRUNCATE}
                          >
                            {CUISINE_LABELS[recipe.cuisine.toLowerCase()] ?? recipe.cuisine}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td visibleFrom="sm">
                        {recipe.difficulty ? (
                          <Badge
                            size="sm"
                            variant="light"
                            color={difficultyColor(recipe.difficulty)}
                            styles={BADGE_NO_TRUNCATE}
                          >
                            {recipe.difficulty}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td visibleFrom="sm">
                        <Text size="sm">{recipe.usageCount}</Text>
                      </Table.Td>
                      <Table.Td visibleFrom="sm">
                        <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(recipe.createdAt)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
        )}
      </Stack>
    </Container>
  );
};
